import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Cake, X } from "lucide-react";
import confetti from "canvas-confetti";

const SEEN_KEY = "cr-birthday-popup-shown";

export default function FreeBirthdayRide() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let seen = false;
    try {
      seen = sessionStorage.getItem(SEEN_KEY) === "1";
    } catch {}
    if (seen) return;

    let timer = null;
    let shown = false;
    const show = () => {
      if (shown) return;
      shown = true;
      setOpen(true);
      try {
        sessionStorage.setItem(SEEN_KEY, "1");
      } catch {}
    };
    // the popup waits 10s; when the soundtrack starts later (browsers block
    // autoplay until the visitor's first tap), the countdown restarts so the
    // music has been playing gently for 10s before the popup appears
    const arm = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(show, 10000);
    };
    const onMusicStarted = () => {
      if (!shown) arm();
    };
    window.addEventListener("cr:music-started", onMusicStarted);
    arm();

    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("cr:music-started", onMusicStarted);
    };
  }, []);

  const close = () => setOpen(false);

  // freeze the page behind the popup so nothing scrolls while it's open
  useEffect(() => {
    if (!open) return;
    const html = document.documentElement;
    const prevHtml = html.style.overflow;
    const prevBody = document.body.style.overflow;
    html.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    if (window.__lenis) window.__lenis.stop();
    return () => {
      html.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
      if (window.__lenis) window.__lenis.start();
    };
  }, [open]);

  return (
    <>
      <button
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          confetti({
            particleCount: 90,
            spread: 75,
            startVelocity: 38,
            scalar: 0.7,
            ticks: 170,
            origin: {
              x: (r.left + r.width / 2) / window.innerWidth,
              y: (r.top + r.height / 2) / window.innerHeight,
            },
            colors: ["#f8b818", "#f9c92e", "#14120c", "#fff7e8", "#d63a2f"],
          });
          setOpen(true);
        }}
        className="group relative h-full w-full rounded-2xl bg-gradient-to-b from-[#f9c92e] to-[#f8b818] p-[3px] shadow-[0_9px_0_#7a5600,0_24px_45px_rgba(36,28,13,0.5)] transition-all duration-150 hover:-translate-y-0.5 hover:shadow-[0_11px_0_#7a5600,0_28px_55px_rgba(36,28,13,0.55)] active:translate-y-[7px] active:shadow-[0_2px_0_#7a5600,0_8px_18px_rgba(36,28,13,0.45)]"
        data-testid="birthday-popup-open-btn"
      >
        <span className="flex h-full w-full flex-col justify-center gap-2 rounded-[13px] bg-[#14120c] p-6 text-left">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#f8b818] text-[#14120c] shadow-[0_4px_0_#7a5600,0_8px_16px_rgba(248,184,24,0.45)]">
            <Cake size={24} />
          </span>
          <span className="text-3xl leading-none text-[#f8b818] drop-shadow-[0_2px_0_rgba(122,86,0,0.9)]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>
            FREE BIRTHDAY RIDE
          </span>
          <span className="text-base font-bold leading-snug text-[#f5ead0]">
            Tap to see how you ride for free.
          </span>
        </span>
      </button>

      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/25 p-4"
          onClick={close}
          data-testid="birthday-popup-modal"
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="relative max-h-[90vh] w-full max-w-[270px] overflow-y-auto overflow-x-hidden rounded-[20px] bg-[#fff7e8] p-5 pt-8 shadow-[0_0_0_2px_#f8b818,0_0_0_6px_rgba(248,184,24,0.16),0_30px_70px_rgba(20,18,12,0.55)]"
            onClick={(e) => e.stopPropagation()}
            data-testid="birthday-popup-card"
          >
            <span className="pointer-events-none absolute inset-x-0 top-0 h-1 rounded-t-[20px] bg-gradient-to-r from-[#f8b818] via-[#f9c92e] to-[#f8b818]" />
            <span className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full bg-[#f8b818]/25 blur-2xl" />
            <button
              onClick={close}
              className="absolute right-3 top-3 rounded-full border border-[#241c0d]/15 bg-white/70 p-1.5 text-[#6b5a3a] transition-colors hover:border-[#c87700] hover:text-[#c87700]"
              aria-label="Close"
              data-testid="birthday-popup-close-button"
            >
              <X size={13} />
            </button>

            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f8b818] text-[#14120c] shadow-[0_3px_0_#7a5600,0_8px_16px_rgba(248,184,24,0.45)]">
              <Cake size={19} />
            </span>
            <p className="mt-3 text-2xl leading-none text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>FREE BIRTHDAY RIDE</p>
            <p className="mt-3 text-sm font-extrabold leading-relaxed text-[#241c0d]" data-testid="birthday-popup-message">
              Enjoy 1 FREE ride during your birthday month. Just bring your original South African ID or birth certificate!
            </p>
            <p className="mt-4 inline-block w-full rounded-xl bg-[#f8b818] px-4 py-2.5 text-center text-lg leading-tight text-[#14120c] shadow-[0_5px_0_#7a5600,0_10px_20px_rgba(36,28,13,0.3)]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }} data-testid="birthday-popup-no-booking">
              NO BOOKING NECESSARY
            </p>
            <p className="mt-2 text-center text-xs font-bold text-[#8a7b5e]" data-testid="birthday-popup-collect">
              Collect at the ticket office onsite.
            </p>

          </motion.div>
        </motion.div>
      )}
    </>
  );
}
